import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const EVENT_IMG =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/268snciq_4B00982C-BDB8-4044-BB1B-7B7FE2243CA8.PNG";

const TYPES = [
    { title: "Birthdays & Milestones", note: "Intimate dinners for 12 – 60." },
    { title: "Engagements & Showers", note: "Cinematic, soft-lit private rooms." },
    { title: "Corporate Off-Sites", note: "Hosted programs, custom menus." },
    { title: "Memorials & Reunions", note: "Held with quiet warmth and care." },
];

const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function PrivateEvents() {
    return (
        <section
            id="private-events"
            data-testid="private-events-section"
            className="relative bg-cream text-charcoal"
            aria-label="Private Events"
        >
            <div className="empire-section">
                <div className="empire-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                        {/* Image */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="lg:col-span-6 relative"
                        >
                            <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden">
                                <img
                                    src={EVENT_IMG}
                                    alt="Empire Jamaica Fusion private dining room — warm burgundy walls, candlelit tables, intimate seating"
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover"
                                    style={{ filter: "contrast(1.06) saturate(1.04) brightness(0.98)" }}
                                />
                                {/* Subtle vignette only — let the warm room speak */}
                                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/25 via-transparent to-charcoal/10" />
                            </div>
                        </motion.div>

                        {/* Text */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.9,
                                delay: 0.15,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="lg:col-span-6"
                        >
                            <p className="empire-eyebrow !text-burgundy">
                                Private Events
                            </p>
                            <h2
                                data-testid="private-events-heading"
                                className="mt-5 empire-subhead text-charcoal"
                            >
                                Quiet luxury,<br />
                                <span className="italic text-burgundy">unforgettable nights.</span>
                            </h2>
                            <p className="mt-6 max-w-xl text-charcoal/70 text-[15px] md:text-base font-light leading-relaxed">
                                Reserve our dining room for an evening built around
                                your guests. Custom menus, the right playlist, warm
                                light, and the kind of service that turns a
                                celebration into a memory.
                            </p>

                            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {TYPES.map((t) => (
                                    <li
                                        key={t.title}
                                        className="border-l border-burgundy/30 pl-5"
                                    >
                                        <div className="font-serif text-xl tracking-tight">
                                            {t.title}
                                        </div>
                                        <div className="text-charcoal/55 text-[13px] font-light mt-1">
                                            {t.note}
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-12 flex flex-col sm:flex-row gap-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        scrollTo("catering-inquiry");
                                        window.dispatchEvent(
                                            new CustomEvent("set-inquiry-type", {
                                                detail: "private_event",
                                            })
                                        );
                                    }}
                                    data-testid="private-events-cta"
                                    className="inline-flex items-center justify-center gap-2 bg-charcoal text-cream px-7 py-3.5 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-burgundy transition-colors duration-300"
                                >
                                    Plan Your Event
                                    <ArrowRight size={14} />
                                </button>
                                <a
                                    href="tel:+18458490497"
                                    data-testid="private-events-call"
                                    className="inline-flex items-center justify-center gap-2 border border-charcoal/40 text-charcoal px-7 py-3.5 text-[12px] uppercase tracking-[0.22em] font-medium hover:border-burgundy hover:text-burgundy transition-colors"
                                >
                                    Speak To A Host
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
